import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import { Refresh, People, EventAvailable, EventNote, Payment } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchPlatformAnalytics } from '../../../store/slices/superAdminSlice';

const PlatformAnalyticsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { analytics, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const fetchData = useCallback(() => { dispatch(fetchPlatformAnalytics()); }, [dispatch]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = [
    { title: 'Total Employees', value: analytics?.totalEmployees ?? 0, icon: <People sx={{ fontSize: 32 }} />, color: '#667eea' },
    { title: 'Attendance Records', value: analytics?.totalAttendanceRecords ?? 0, icon: <EventAvailable sx={{ fontSize: 32 }} />, color: '#4caf50' },
    { title: 'Leave Requests', value: analytics?.totalLeaveRequests ?? 0, icon: <EventNote sx={{ fontSize: 32 }} />, color: '#ff9800' },
    { title: 'Payroll Runs', value: analytics?.totalPayrollRuns ?? 0, icon: <Payment sx={{ fontSize: 32 }} />, color: '#9c27b0' },
  ];

  const maxEmployees = analytics?.tenantUsages?.length
    ? Math.max(...analytics.tenantUsages.map((t) => t.employeeCount))
    : 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Platform Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Aggregated metrics across all tenants</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statCards.map((card, i) => (
          <Grid size={{ xs: 6, sm: 3 }} key={i}>
            {loading ? (
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            ) : (
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Box sx={{ height: 3, background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}88 100%)` }} />
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                  <Box sx={{ background: `${card.color}14`, borderRadius: 2.5, minWidth: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color, '& .MuiSvgIcon-root': { fontSize: { xs: 22, sm: 32 } } }}>
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800} lineHeight={1.1} sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>{card.value.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap sx={{ display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{card.title}</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Tenant Usage Summary */}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>Tenant Usage Summary</Typography>
        {loading ? (
          <Skeleton variant="rounded" height={300} />
        ) : !analytics?.tenantUsages?.length ? (
          <Typography color="text.secondary" textAlign="center" py={4}>No usage data available</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employees</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Attendance</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Leave Requests</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 200 }}>Employee Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.tenantUsages.map((tenant) => (
                  <TableRow key={tenant.tenantId} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{tenant.tenantName}</Typography></TableCell>
                    <TableCell>{tenant.employeeCount}</TableCell>
                    <TableCell>{tenant.attendanceRecords.toLocaleString()}</TableCell>
                    <TableCell>{tenant.leaveRequests.toLocaleString()}</TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={(tenant.employeeCount / maxEmployees) * 100}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default PlatformAnalyticsPage;
